/**
 * Renders a responsive 16:9 embed for any YouTube or Vimeo URL.
 * If url is empty/null, shows an elegant "video coming soon" tile so listings
 * remain visually complete before real footage is delivered.
 *
 * Accepted URL shapes:
 *   • https://www.youtube.com/watch?v=ID
 *   • https://youtu.be/ID
 *   • https://youtube.com/embed/ID
 *   • https://vimeo.com/ID
 *   • https://player.vimeo.com/video/ID
 */

import { PlayCircle } from "lucide-react";

const parseVideo = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    // YouTube variants
    if (host === "youtu.be") {
      const id = u.pathname.slice(1);
      return id ? { provider: "youtube", id } : null;
    }
    if (host.endsWith("youtube.com")) {
      if (u.pathname.startsWith("/embed/")) {
        return { provider: "youtube", id: u.pathname.split("/")[2] };
      }
      const v = u.searchParams.get("v");
      return v ? { provider: "youtube", id: v } : null;
    }
    // Vimeo
    if (host.endsWith("vimeo.com")) {
      const parts = u.pathname.split("/").filter(Boolean);
      const id = parts[parts.length - 1];
      return id && /^\d+$/.test(id) ? { provider: "vimeo", id } : null;
    }
  } catch {
    return null;
  }
  return null;
};

const embedSrc = ({ provider, id }) => {
  if (provider === "youtube") {
    return `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`;
  }
  if (provider === "vimeo") {
    return `https://player.vimeo.com/video/${id}?byline=0&title=0`;
  }
  return null;
};

export const VideoEmbed = ({ url, title = "Property tour", testId }) => {
  const parsed = parseVideo(url);
  const src = parsed && embedSrc(parsed);

  if (!src) {
    return (
      <div
        className="relative flex flex-col items-center justify-center overflow-hidden text-center"
        style={{
          aspectRatio: "16 / 9",
          borderRadius: 18,
          backgroundColor: "#24382E",
          color: "#EDE9E0",
        }}
        data-testid={testId}
      >
        <div className="grain absolute inset-0 opacity-40" />
        <PlayCircle size={44} strokeWidth={1.2} color="#C89A4E" />
        <p
          className="label-caps mt-4 text-[11px]"
          style={{ color: "rgba(237,233,224,0.72)" }}
        >
          {title} — coming soon
        </p>
      </div>
    );
  }

  return (
    <div
      className="relative overflow-hidden"
      style={{
        aspectRatio: "16 / 9",
        borderRadius: 18,
        backgroundColor: "#141E19",
      }}
      data-testid={testId}
    >
      <iframe
        title={title}
        src={src}
        className="absolute inset-0 h-full w-full"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
      />
    </div>
  );
};

export default VideoEmbed;
